/**
 * Admin Developers page — onboard, list, and manage developer accounts.
 *
 * "Onboard Developer" is the primary action: creates Cognito user +
 * DynamoDB record + first API key in a single step.
 */

import React, { useEffect, useState, useCallback } from 'react';
import * as adminService from '../../services/admin.service';
import {
  Plus,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Key,
  Copy,
  Trash2,
  Eye,
  X,
  ShieldAlert,
} from 'lucide-react';

export default function AdminDevelopersPage() {
  const [developers, setDevelopers] = useState<adminService.AdminDeveloper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  // Onboard modal
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardEmail, setOnboardEmail] = useState('');
  const [onboardName, setOnboardName] = useState('');
  const [onboardCompany, setOnboardCompany] = useState('');
  const [onboardTier, setOnboardTier] = useState('free');
  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState<adminService.OnboardResult | null>(null);

  // API Keys drawer
  const [selectedDevId, setSelectedDevId] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<adminService.AdminApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyEnv, setNewKeyEnv] = useState('sandbox');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<adminService.AdminDeveloper | null>(null);
  const [deleteFundsConfirmed, setDeleteFundsConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadDevelopers = useCallback((cursor?: string) => {
    setLoading(true);
    adminService
      .listDevelopers(25, cursor)
      .then((result) => {
        setDevelopers((prev) => (cursor ? [...prev, ...result.developers] : result.developers));
        setNextCursor(result.nextCursor);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDevelopers();
  }, [loadDevelopers]);

  // ── Onboard ──
  const handleOnboard = async () => {
    setOnboarding(true);
    setError('');
    try {
      const result = await adminService.onboardDeveloper({
        email: onboardEmail,
        name: onboardName,
        namespace: onboardCompany || undefined,
        tier: onboardTier,
      });
      setOnboardResult(result);
      loadDevelopers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Onboarding failed');
    } finally {
      setOnboarding(false);
    }
  };

  const resetOnboard = () => {
    setShowOnboard(false);
    setOnboardResult(null);
    setOnboardEmail('');
    setOnboardName('');
    setOnboardCompany('');
    setOnboardTier('free');
  };

  // ── Status toggle ──
  const handleStatusToggle = async (dev: adminService.AdminDeveloper) => {
    const newStatus = dev.status === 'active' ? 'suspended' : 'active';
    try {
      await adminService.updateDeveloper(dev.developerId, { status: newStatus });
      loadDevelopers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update developer');
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget || !deleteFundsConfirmed) return;
    setDeleting(true);
    setError('');
    try {
      await adminService.deleteDeveloper(deleteTarget.developerId, true);
      setDeleteTarget(null);
      setDeleteFundsConfirmed(false);
      loadDevelopers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ── API Keys ──
  const openKeys = async (devId: string) => {
    setSelectedDevId(devId);
    setKeysLoading(true);
    setCreatedKey(null);
    try {
      const result = await adminService.listDeveloperApiKeys(devId);
      setApiKeys(result.apiKeys);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setKeysLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!selectedDevId || !newKeyName) return;
    try {
      const result = await adminService.createDeveloperApiKey(selectedDevId, {
        name: newKeyName,
        environment: newKeyEnv,
      });
      setCreatedKey(result.fullKey);
      setNewKeyName('');
      // Refresh list
      const refreshed = await adminService.listDeveloperApiKeys(selectedDevId);
      setApiKeys(refreshed.apiKeys);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!selectedDevId) return;
    try {
      await adminService.revokeDeveloperApiKey(selectedDevId, keyId);
      const refreshed = await adminService.listDeveloperApiKeys(selectedDevId);
      setApiKeys(refreshed.apiKeys);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="apikeys-page">
      <div className="page-header">
        <div>
          <h1>Developers</h1>
          <p>Onboard and manage developer accounts</p>
        </div>
        <button className="btn btn-accent" onClick={() => setShowOnboard(true)}>
          <Plus size={16} /> Onboard Developer
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} /> {error}
          <button className="btn btn-ghost btn-sm" onClick={() => setError('')} style={{ marginLeft: 'auto' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ── Onboard Modal ── */}
      {showOnboard && (
        <div className="modal-overlay" onClick={resetOnboard}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            {onboardResult ? (
              <>
                <h2><CheckCircle2 size={20} color="var(--success)" /> Developer Onboarded</h2>
                <p style={{ fontSize: 14, color: 'var(--text-dim)', margin: '8px 0 16px' }}>
                  {onboardResult.message}
                </p>
                <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 16, fontSize: 13 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Cognito User:</strong> {onboardResult.cognitoUsername}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>API Key (save now — shown only once):</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{ flex: 1, background: 'var(--bg-soft)', padding: '8px 12px', borderRadius: 6, wordBreak: 'break-all', fontSize: 12 }}>
                      {onboardResult.apiKey.fullKey}
                    </code>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => copyToClipboard(onboardResult.apiKey.fullKey)}
                      title="Copy"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <div className="modal-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-accent" onClick={resetOnboard}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h2>Onboard Developer</h2>
                <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '4px 0 16px' }}>
                  Creates a Cognito user (sends email invite), DynamoDB record, and first API key in one step.
                </p>
                <div className="form-group">
                  <label>Email</label>
                  <input placeholder="dev@company.com" type="email" value={onboardEmail} onChange={(e) => setOnboardEmail(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input placeholder="Full name" value={onboardName} onChange={(e) => setOnboardName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Company <span className="optional">(optional)</span></label>
                  <input placeholder="Company name" value={onboardCompany} onChange={(e) => setOnboardCompany(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Tier</label>
                  <select value={onboardTier} onChange={(e) => setOnboardTier(e.target.value)}>
                    <option value="free">Free</option>
                    <option value="pro">Pro (paid)</option>
                    <option value="enterprise">Enterprise (Custom)</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-ghost" onClick={resetOnboard}>Cancel</button>
                  <button
                    className="btn btn-accent"
                    onClick={handleOnboard}
                    disabled={onboarding || !onboardEmail || !onboardName}
                  >
                    {onboarding ? 'Onboarding…' : 'Onboard'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── API Keys Drawer ── */}
      {selectedDevId && (
        <div className="modal-overlay" onClick={() => setSelectedDevId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2><Key size={18} /> API Keys</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedDevId(null)}>
                <X size={16} />
              </button>
            </div>

            {createdKey && (
              <div className="alert alert-success" style={{ marginTop: 12 }}>
                <CheckCircle2 size={14} /> Key created (copy now — shown once):
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <code style={{ flex: 1, fontSize: 11, wordBreak: 'break-all' }}>{createdKey}</code>
                  <button className="btn btn-ghost btn-sm" onClick={() => copyToClipboard(createdKey)}>
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* New key form */}
            <div className="key-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Key Name</label>
                <input
                  placeholder="e.g. Production Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Environment</label>
                <select value={newKeyEnv} onChange={(e) => setNewKeyEnv(e.target.value)}>
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <button className="btn btn-accent key-form-btn" onClick={handleCreateKey} disabled={!newKeyName}>
                <Plus size={14} /> Add
              </button>
            </div>

            {/* Keys table */}
            {keysLoading ? (
              <div className="page-loader">Loading…</div>
            ) : (
              <div className="table-container">
              <table className="data-table" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Env</th>
                    <th>Status</th>
                    <th>Last Used</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.name || '—'}</td>
                      <td><code style={{ fontSize: 11 }}>{k.keyPrefix}…</code></td>
                      <td><span className={`env-badge env-${k.environment}`}>{k.environment}</span></td>
                      <td>
                        <span className={`status-dot ${k.status === 'active' ? 'active' : 'inactive'}`} />
                        {k.status}
                      </td>
                      <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}</td>
                      <td>
                        {k.status === 'active' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleRevokeKey(k.id)}
                            title="Revoke"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {apiKeys.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty-state">No API keys.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => { setDeleteTarget(null); setDeleteFundsConfirmed(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldAlert size={20} style={{ color: 'var(--danger)' }} /> Delete Developer
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '8px 0 16px' }}>
              This will permanently delete <strong>{deleteTarget.email}</strong>, revoke all their API keys,
              and remove their Cognito account. This action cannot be undone.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, marginBottom: 16 }}>
              <div><strong>Name:</strong> {deleteTarget.name || '—'}</div>
              <div><strong>Tier:</strong> {deleteTarget.tier}</div>
              <div><strong>Status:</strong> {deleteTarget.status}</div>
              <div><strong>Created:</strong> {new Date(deleteTarget.createdAt).toLocaleDateString()}</div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '12px 16px', background: 'var(--bg-soft)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <input
                type="checkbox"
                checked={deleteFundsConfirmed}
                onChange={(e) => setDeleteFundsConfirmed(e.target.checked)}
                style={{ accentColor: 'var(--danger)' }}
              />
              <span>I confirm that all funds have been repaid and reconciled for this developer account.</span>
            </label>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setDeleteTarget(null); setDeleteFundsConfirmed(false); }}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={!deleteFundsConfirmed || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Developer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Developers Table ── */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {developers.map((dev) => (
              <tr key={dev.developerId}>
                <td className="cell-primary">{dev.name || '—'}</td>
                <td>{dev.email}</td>
                <td><span className={`env-badge env-${dev.tier}`}>{dev.tier}</span></td>
                <td>
                  <span className={`status-dot ${dev.status === 'active' ? 'active' : 'inactive'}`} />
                  {dev.status}
                </td>
                <td>{new Date(dev.createdAt).toLocaleDateString()}</td>
                <td className="actions-cell">
                  <button className="btn btn-ghost btn-sm" onClick={() => openKeys(dev.developerId)} title="API Keys">
                    <Key size={14} />
                  </button>
                  <button
                    className={`btn btn-sm ${dev.status === 'active' ? 'btn-ghost' : 'btn-accent'}`}
                    onClick={() => handleStatusToggle(dev)}
                  >
                    {dev.status === 'active' ? 'Suspend' : 'Activate'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setDeleteTarget(dev)}
                    title="Delete developer"
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {developers.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="empty-state">No developers yet. Click &quot;Onboard Developer&quot; to add one.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && <div className="page-loader">Loading…</div>}

      {nextCursor && !loading && (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <button className="btn btn-ghost" onClick={() => loadDevelopers(nextCursor)}>
            Load More <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
